// Compare stash — global context for stashing lenders into a side-by-side
// comparison drawer. Persists to localStorage so the stash survives
// reloads and route changes.
//
// Shape stored per lender:
//   { slug, name, category, min_apr, max_apr, rating, min_credit, best_for, badge }
//
// We cap the stash at 4 lenders (industry sweet-spot — beyond 4, side-by-
// side comparisons stop being useful on a laptop screen). New adds past
// the cap silently no-op + bump existing lender to most-recent position.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";
import { track, events } from "./analytics";
import { fetchLenderRates } from "./api";

const STORAGE_KEY = "carsavr:compare-stash";
const MAX_STASH = 4;

// Iter-93 — Human-readable category labels shared with CompareDrawer + the
// "Switch stash?" toast. Centralised here so the stash → drawer → modal all
// stay in lockstep when we add a new vertical (e.g. tires, EV chargers).
export const COMPARE_CATEGORY_LABELS = {
  insurance: "Auto Insurance",
  "auto-loan": "Auto Loans",
  refinance: "Refinance",
  warranty: "Warranty",
};

function categoryLabel(cat) {
  return COMPARE_CATEGORY_LABELS[cat] || cat || "this category";
}

function readStash() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => x && typeof x === "object" && x.slug && x.name)
      .slice(0, MAX_STASH);
  } catch {
    return [];
  }
}

function writeStash(items) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* localStorage quota / private mode — fail silently */
  }
}

const CompareStashContext = createContext({
  items: [],
  isStashed: () => false,
  add: () => {},
  remove: () => {},
  clear: () => {},
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
});

export function CompareStashProvider({ children }) {
  const [items, setItems] = useState(() => readStash());
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  // Persist on every change.
  useEffect(() => {
    writeStash(items);
  }, [items]);

  // Show the drawer automatically the first time something is stashed in
  // this session. After that, the user controls open/close.
  useEffect(() => {
    if (items.length > 0 && !isDrawerOpen) {
      setDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length > 0]);

  // Iter-96 — Shareable URL hydration. If the visitor lands on any path
  // with ?stash=slug1,slug2,... we fetch all lenders, match by slug,
  // overwrite the current stash, and auto-open the modal. URL is then
  // cleaned via history.replaceState so a reload doesn't double-hydrate.
  //
  // Guardrails:
  //   • Single-category lock — mixed-category URLs are rejected with a
  //     toast (matches iter-93's stash policy). Iterates the matched
  //     lenders' categories; if not all equal, hydration aborts.
  //   • Cap at MAX_STASH (4) — drops extras silently.
  //   • Network failure / no matches — silent no-op; the user just sees
  //     no stash and their existing local stash stays intact.
  //   • Runs once on mount only. Subsequent navigations to URLs with the
  //     same param won't re-fire (we clean the query on success).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const stashParam = params.get("stash");
    if (!stashParam) return;
    const slugs = stashParam
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, MAX_STASH);
    if (slugs.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await fetchLenderRates();
        if (cancelled) return;
        // Preserve the sender's slug order — that's the comparison they
        // wanted you to see.
        const found = slugs
          .map((slug) => all.find((r) => r.slug === slug))
          .filter(Boolean);
        if (found.length === 0) {
          toast.error("Shared compare link had no matching lenders.");
          return;
        }
        const firstCat = found[0].category;
        const allSameCat = found.every((r) => r.category === firstCat);
        if (!allSameCat) {
          toast.error(
            "Shared compare link mixed categories — keep your existing stash.",
          );
          return;
        }
        const stashEntries = found.map((rate) => ({
          slug: rate.slug,
          name: rate.name,
          category: rate.category,
          min_apr: rate.min_apr,
          max_apr: rate.max_apr,
          rating: rate.rating,
          min_credit: rate.min_credit,
          best_for: rate.highlight || rate.best_for || null,
          badge: rate.badge || rate.editor_pick || null,
          details: rate.details || null,
        }));
        setItems(stashEntries);
        setModalOpen(true);
        track(events.COMPARE_STASH_HYDRATED, {
          count: stashEntries.length,
          category: firstCat,
          slugs: stashEntries.map((s) => s.slug).join(","),
        });
        // Clean the URL so a reload doesn't re-hydrate / re-toast.
        params.delete("stash");
        const newQs = params.toString();
        const newUrl =
          window.location.pathname +
          (newQs ? `?${newQs}` : "") +
          window.location.hash;
        window.history.replaceState({}, "", newUrl);
      } catch {
        // Silent — visitor isn't blocked, just doesn't get hydration.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isStashed = useCallback(
    (slug) => items.some((it) => it.slug === slug),
    [items],
  );

  const add = useCallback((lender) => {
    if (!lender || !lender.slug) return;
    setItems((prev) => {
      // Iter-93 — Single-category compare lock. Once the stash has lenders
      // from one category (e.g. "auto-loan"), adding a lender from a
      // different category (e.g. "insurance") would produce a
      // comparison that doesn't make sense (APR vs premium etc.). Instead
      // of silently swapping or blocking, we surface a clear action toast:
      //   • Keep current stash → noop on the add
      //   • Switch to {newCategory} → replace stash with just the new lender
      const stashCategory = prev[0]?.category;
      const incomingCategory = lender.category;
      if (
        stashCategory &&
        incomingCategory &&
        stashCategory !== incomingCategory &&
        // Allow refinance ↔ auto-loan if user really wants — they share
        // most fields. Mixing insurance + auto-loan is the dangerous one.
        !(
          (stashCategory === "auto-loan" && incomingCategory === "refinance") ||
          (stashCategory === "refinance" && incomingCategory === "auto-loan")
        )
      ) {
        const oldLabel = categoryLabel(stashCategory);
        const newLabel = categoryLabel(incomingCategory);
        toast(
          `You're comparing ${oldLabel}. Switch the stash to ${newLabel}?`,
          {
            duration: 8000,
            action: {
              label: `Switch to ${newLabel}`,
              onClick: () => {
                setItems([lender]);
                track(events.COMPARE_STASH_ADD, {
                  slug: lender.slug,
                  name: lender.name,
                  category: lender.category,
                  category_switched_from: stashCategory,
                });
              },
            },
            cancel: {
              label: "Keep stash",
              onClick: () => {},
            },
          },
        );
        // Don't mutate the stash — wait for the user's toast action.
        return prev;
      }
      const without = prev.filter((it) => it.slug !== lender.slug);
      const next = [...without, lender].slice(-MAX_STASH);
      return next;
    });
    track(events.COMPARE_STASH_ADD, {
      slug: lender.slug,
      name: lender.name,
      category: lender.category,
    });
  }, []);

  const remove = useCallback((slug) => {
    setItems((prev) => prev.filter((it) => it.slug !== slug));
    track(events.COMPARE_STASH_REMOVE, { slug });
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    track(events.COMPARE_STASH_CLEAR, {});
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openModal = useCallback(() => {
    setModalOpen(true);
    track(events.COMPARE_STASH_VIEW, { count: items.length });
  }, [items.length]);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const value = useMemo(
    () => ({
      items,
      isStashed,
      add,
      remove,
      clear,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      isModalOpen,
      openModal,
      closeModal,
      maxStash: MAX_STASH,
    }),
    [items, isStashed, add, remove, clear, isDrawerOpen, openDrawer, closeDrawer, isModalOpen, openModal, closeModal],
  );

  return (
    <CompareStashContext.Provider value={value}>
      {children}
    </CompareStashContext.Provider>
  );
}

export function useCompareStash() {
  return useContext(CompareStashContext);
}
